import React, {createContext, ReactNode, useContext, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {VideoSubjectMetadataMessage} from '@/types/VideoSubjectMetadata';
import {videoPath} from 'routes';

// Combined playback and queue-drawer context
export interface WatchContextValue {
  currentVideo?: VideoSubjectMetadataMessage;
  /** Facade queue of videos for display purposes */
  facadeQueue: VideoSubjectMetadataMessage[];
  /** Shadow queue of videos for back-up purposes */
  shadowQueue: VideoSubjectMetadataMessage[];
  isQueueMatch(list: VideoSubjectMetadataMessage[]): boolean;
  playbackTimeRef: React.MutableRefObject<number>;
  isPlaying: boolean;
  // Playback controls
  play(
    video:        VideoSubjectMetadataMessage,
    facadeQueue:  VideoSubjectMetadataMessage[],
    shadowQueue?: VideoSubjectMetadataMessage[],
  ): void;
  pause(): void;
  resume(): void;
  toggle(): void;
  stop(): void;
  // Go to the next video in queue without navigation
  playNext(): void;
  // Go to the prev video in queue without navigation
  playPrev(): void;
  // Navigate to the next video's page; replace in history if replace=true
  navigateNext(replace?: boolean): void;
  // Navigate to the previous video's page; replace in history if replace=true
  navigatePrev(replace?: boolean): void;
  // Navigate to the prev video's page in browser history
  navigateBack(): void;
  seek(time: number): void;
  // Queue drawer controls
  drawerOpen: boolean;
  openDrawer(): void;
  closeDrawer(): void;
  // Peek drawer: briefly open then close for visual cue
  peekDrawer(): void;
  // Direct queue manipulation: resets both facade and shadow queues
  setQueue(queue: VideoSubjectMetadataMessage[]): void;
  /** Shuffle facade queue preserving current video at index 0, shuffling the rest */
  shuffleQueue(): void;
}

const WatchContext = createContext<WatchContextValue | undefined>(undefined);

/**
 * Provides playback and queue-drawer state across the app.
 */
export const PlaybackProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [currentVideo, setCurrentVideo] = useState<VideoSubjectMetadataMessage | undefined>(undefined);
  // Facade queue for display
  const [facadeQueue, setFacadeQueue] = useState<VideoSubjectMetadataMessage[]>([]);
  // Shadow queue for order-sensitive matching
  const [shadowQueue, setShadowQueue] = useState<VideoSubjectMetadataMessage[]>([]);
  /**
   * Direct queue manipulation: resets both facade and shadow queues
   */
  const setQueue = (q: VideoSubjectMetadataMessage[]) => {
    setFacadeQueue(q);
    setShadowQueue(q);
  };
  /**
   * Checks whether a list of videos is order-sensitively equal to the current facade queue
   */
  const isQueueMatch = (list: VideoSubjectMetadataMessage[]): boolean => {
    if (list.length !== facadeQueue.length) return false;
    for (let i = 0; i < list.length; i++) {
      if (list[i].musicVideo !== facadeQueue[i].musicVideo) return false;
    }
    return true;
  };
  const playbackTimeRef = useRef<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  /**
   * Start playing a video, resetting queues. facadeQ for display, shadowQ for matching (defaults to facadeQ).
   */
  const play = (
    video   : VideoSubjectMetadataMessage,
    facadeQ : VideoSubjectMetadataMessage[],
    shadowQ?: VideoSubjectMetadataMessage[],
  ) => {
    setCurrentVideo(video);
    setFacadeQueue(facadeQ);
    setShadowQueue(shadowQ ?? facadeQ);
    playbackTimeRef.current = 0;
    setIsPlaying(true);
  };
  const pause  = () => setIsPlaying(false);
  const resume = () => setIsPlaying(true);
  const toggle = () => setIsPlaying((v) => !v);
  const stop   = () => {
    setCurrentVideo(undefined);
    playbackTimeRef.current = 0;
    setIsPlaying(false);
  };
  // Go to the next video in queue without navigation
  const next = (): VideoSubjectMetadataMessage | undefined => {
    if (!currentVideo) return;
    if (facadeQueue.length === 0) { stop(); return; }
    const playIdx = facadeQueue.findIndex((v) => v.musicVideo === currentVideo.musicVideo);
    const nextIdx = playIdx < 0 ? 0 : (facadeQueue.length + playIdx + 1) % facadeQueue.length;
    return facadeQueue[nextIdx];
  };
  // Go to the prev video in queue without navigation
  const prev = (): VideoSubjectMetadataMessage | undefined => {
    if (!currentVideo) return;
    if (facadeQueue.length === 0) { stop(); return; }
    const playIdx = facadeQueue.findIndex((v) => v.musicVideo === currentVideo.musicVideo);
    const prevIdx = playIdx < 1 ? 0 : (facadeQueue.length + playIdx - 1) % facadeQueue.length;
    return facadeQueue[prevIdx];
  };
  const playNext = () => {
    const nextVid = next();
    if (!nextVid) return;
    play(nextVid, facadeQueue, shadowQueue);
  };
  const playPrev = () => {
    const prevVid = prev();
    if (!prevVid) return;
    play(prevVid, facadeQueue, shadowQueue);
  };
  // Navigate to the next video's page, passing both facade and shadow queues
  const navigateNext = (replace: boolean = false) => {
    const nextVid = next();
    if (!nextVid) return;
    navigate(
      videoPath(nextVid.musicVideo),
      {
        replace,
        state: {
          musicVideo: nextVid.musicVideo,
          facadeQueue,
          shadowQueue,
        },
      }
    );
  };
  // Navigate to the previous video's page, passing both facade and shadow queues
  const navigatePrev = (replace: boolean = false) => {
    const prevVid = prev();
    if (!prevVid) return;
    navigate(
      videoPath(prevVid.musicVideo),
      {
        replace,
        state: {
          musicVideo: prevVid.musicVideo,
          facadeQueue,
          shadowQueue,
        },
      }
    );
  };
  // Navigate to the prev video's page in browser history
  const navigateBack = () => navigate(-1);
  const seek = (time: number) => {
    playbackTimeRef.current = time;
  };
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);
  /**
   * Peek drawer: open the queue drawer without auto-closing (no animation cycle)
   */
  const peekDrawer = () => {
    if (!drawerOpen) {
      openDrawer();
    }
  };
  /**
   * Shuffle the play queue: if currentVideo is in the queue, move it to index 0
   * and shuffle remaining videos. Otherwise, shuffle the entire queue.
   */
  const shuffleQueue = () => {
    const exponent = 0.0; // equal weight!
    const weightedShuffle = (items: VideoSubjectMetadataMessage[]): VideoSubjectMetadataMessage[] => {
      return items
        .map(item => {
          const weight = Math.pow(item.videoScore, exponent);
          const key = Math.pow(Math.random(), 1 / weight);
          return { item, key };
        })
        .sort((a, b) => b.key - a.key)
        .map(({ item }) => item);
    };
    const q = facadeQueue;
    if (currentVideo) {
      const idx = q.findIndex(v => v.musicVideo === currentVideo.musicVideo);
      if (idx >= 0) {
        // Preserve current video at front, shuffle the rest
        const rest = q.filter(v => v.musicVideo !== currentVideo.musicVideo);
        const shuffled = weightedShuffle(rest);
        const newQueue = [currentVideo, ...shuffled];
        // Update both facade and shadow queues
        setFacadeQueue(newQueue);
        setShadowQueue(newQueue);
        return;
      }
    }
    // Shuffle entire queue if no current video match
    const fullShuffled = weightedShuffle([...q]);
    setFacadeQueue(fullShuffled);
    setShadowQueue(fullShuffled);
  };

  return (
      <WatchContext.Provider
        value={{
        currentVideo,
        /** Facade queue for display  */
        facadeQueue,
        /** Shadow queue for matching */
        shadowQueue,
        playbackTimeRef,
        isPlaying,
        play,
        pause,
        resume,
        toggle,
        stop,
        playNext,
        playPrev,
        navigateNext,
        navigatePrev,
        navigateBack,
        seek,
        drawerOpen,
        openDrawer,
        closeDrawer,
        peekDrawer,
        setQueue,
        shuffleQueue,
        isQueueMatch,
      }}
    >
      {children}
    </WatchContext.Provider>
  );
};

/**
 * Hook to access playback/queue context. Must be used within PlaybackProvider.
 */
export const usePlayback = (): WatchContextValue => {
  const ctx = useContext(WatchContext);
  if (!ctx) throw new Error('usePlayback must be used within a PlaybackProvider');
  return ctx;
};